/**
 * These are helper parameters for platform reactions counts.
 * In grouse these get mapped to the appropriate MentionField enum that will include sql to get the
 * reaction count as well as the mention id
 * @type {string[]}
 */
const userSafePlatformReactionsCountSelectParameters = ["countByFacebookReactions", "countByInstagramReactions",
    "countByLinkedInReactions", "countByTwitterReactions", "countByTikTokReactions", "countByFacebookVideoViews"
];

/**
 * These are helper parameters for platform reactions volumes.
 * In grouse these get mapped to the appropriate MentionField enum that will include sql to get the
 * reaction volume as well as the mention id
 * @type {string[]}
 */
const userSafePlatformReactionsVolumeSelectParameters = ["volumeByFacebookReactions", "volumeByInstagramReactions",
    "volumeByLinkedInReactions", "volumeByTwitterReactions", "volumeByTikTokReactions"
];

/**
 * These are the actual MentionField enum fields that are used in grouse for Facebook reactions counts.
 * @type {string[]}
 */
export const beFacebookSelects = [
    'facebookLikes',
    'facebookWow',
    'facebookSad',
    'facebookCare',
    'facebookFire',
    'facebookHaha',
    'facebookLove',
    'facebookAngry',
    'facebookPride',
    'facebookThankful',
    'facebookHundred',
    'facebookCommentCount',
    'facebookVideoViews'
];

/**
 * These are the actual MentionField enum fields that are used in grouse for Instagram reactions counts.
 * @type {string[]}
 */
export const beInstagramSelects = [
    'instagramLikes',
    'instagramCommentCount',
    'instagramVideoViews'
];

/**
 * These are the actual MentionField enum fields that are used in grouse for LinkedIn reactions counts.
 * @type {string[]}
 */
export const beLinkedInSelects = [
    'linkedInLikes',
    'linkedInPraises',
    'linkedInEntertainments',
    'linkedInEmpathy',
    'linkedInInterests',
    'linkedInAppreciations',
    'linkedInCommentCount',
    'linkedInVideoViews'
];

/**
 * These are the actual MentionField enum fields that are used in grouse for Twitter reactions counts.
 * @type {string[]}
 */
export const beTwitterSelects = [
    'twitterLikes',
    'twitterCommentCount',
    'twitterVideoViews'
];

/**
 * These are the actual MentionField enum fields that are used in grouse for TikTok reactions counts.
 * @type {string[]}
 */
export const beTikTokSelects = [
    'tikTokLikes',
    'tikTokCommentCount',
    'tikTokVideoViews'
];

const reactionTypeMapping = {
    like: 'Like',
    wow: 'Wow',
    sad: 'Sad',
    care: 'Care',
    fire: 'Fire',
    haha: 'Haha',
    love: 'Love',
    angry: 'Angry',
    pride: 'Pride',
    comment: 'Comment',
    thankful: 'Thankful',
    hundred: 'Hundred',
    praise: "Praise",
    entertainment: "Entertainment",
    empathy: "Empathy",
    interest: "Interest",
    appreciation: "Appreciation",
    'videoViews': 'Video Views'
};

// Platform-specific reaction type mappings
const platformReactionTypes = {
    facebookReactions: ['like', 'wow', 'sad', 'care', 'fire', 'haha', 'love', 'angry', 'thankful', 'pride', 'hundred', 'comment', 'videoViews'],
    instagramReactions: ['like', 'comment', 'videoViews'],
    linkedInReactions: ['like', 'praise', 'entertainment', 'empathy', 'interest', 'appreciation', 'comment', 'videoViews'],
    twitterReactions: ['like', 'comment', 'videoViews'],
    tikTokReactions: ['like', 'comment', 'videoViews'],
    unknownReactions: Object.keys(reactionTypeMapping) // fallback to all types
};

/**
 *
 * @param {String} fields array of select fields (what data would you like to see)
 * @returns {Boolean} the first user safe select field, or undefined if not found
 */
export function isPlatformReactionUserSafeField(fields) {
    return !!fields.split(',').find(field => userSafePlatformReactionsCountSelectParameters.includes(field)
        || !!userSafePlatformReactionsVolumeSelectParameters.includes(field))
}

/**
 * Checks if the field is a user safe platform reactions count field
 * @param {String[]} fields
 * @return {boolean}
 */
export function isPlatformReactionUserSafeCountField(fields) {
    return !!userSafePlatformReactionsCountSelectParameters.find(field => fields.includes(field))
}

/**
 * Checks if the field is a user safe platform reactions count field
 * @param {String[]} fields
 * @return {string}
 */
export function getPlatformReactionUserSafeCountField(fields) {
    return userSafePlatformReactionsCountSelectParameters.find(field => fields.includes(field))
}

/**
 * Checks if the field is a user safe platform reactions volume field
 * @param {String[]} fields
 * @return {boolean}
 */
export function isPlatformReactionUserSafeVolumeField(fields) {
    return !!userSafePlatformReactionsVolumeSelectParameters.find(field => fields.includes(field))
}

/**
 * Gets the field is a user safe platform reactions volume field
 * @param {String[]} fields
 * @return {String}
 */
export function getPlatformReactionUserSafeVolumeField(fields) {
    return userSafePlatformReactionsVolumeSelectParameters.find(field => fields.includes(field))
}

/**
 *
 * @param {String} field the user safe platform reaction parameter (what data would you like to see)
 * @returns {String} the first user safe select field, or undefined if not found
 */
export function getPlatformReactionType(field) {

    if (isPlatformReactionUserSafeCountField(field)) {
        return "count";
    } else if (isPlatformReactionUserSafeVolumeField(field)) {
        return "volume";
    }
}

/**
 *
 * @param {String} field the user safe platform reaction parameter (what data would you like to see)
 * @param {{}} dataForThePeriod
 * @returns {{platformType: string, reactions: {}, reactionTypeMapping: {}}} the platform data with reaction mapping
 */
export function getPlatform(field, dataForThePeriod) {
    let platformType;
    
    if (field.includes('Facebook')) {
        platformType = 'facebookReactions';
    } else if (field.includes('Instagram')) {
        platformType = 'instagramReactions';
    } else if (field.includes('LinkedIn')) {
        platformType = 'linkedInReactions';
    } else if (field.includes('Twitter')) {
        platformType = 'twitterReactions';
    } else if (field.includes('TikTok')) {
        platformType = 'tikTokReactions';
    } else {
        // Default fallback if platform not recognized
        platformType = 'unknownReactions';
    }
    
    return {
        platformType: platformType,
        reactions: dataForThePeriod,
        reactionTypeMapping: reactionTypeMapping
    };
}

/**
 * Gets the reaction types that are relevant for a specific platform
 * @param {String} platformType - The platform type (e.g., 'facebookReactions', 'instagramReactions')
 * @returns {String[]} Array of reaction type keys for the platform
 */
export function getReactionTypesForPlatform(platformType) {
    return platformReactionTypes[platformType] || platformReactionTypes.unknownReactions;
}

export { reactionTypeMapping, platformReactionTypes };


export function expandPlatformReactionsVolumeRow(cleanQuery, grouseResponse) {
    let expandedData = [];
    const val = getPlatform(cleanQuery.select, grouseResponse);

    if (val.reactions) {
        const reactions = val.reactions;

        if (reactions && typeof reactions === 'object') {
            // Iterate through platform-specific reaction types and extract their count values
            const reactionTypes = getReactionTypesForPlatform(val.platformType);

            for (const reactionType of reactionTypes) {
                const reactionCount = reactions[reactionType] || 0;
                const reactionDisplayName = reactionTypeMapping[reactionType];
                const fantasticChartProps = {
                    [val.platformType]: reactionCount,
                    reactionType: {id: reactionType, name: reactionDisplayName},
                    'engagementType.id': reactionType,
                    'engagementType.name': reactionDisplayName
                };

                let c = {...grouseResponse[reactionType], ...grouseResponse[`${reactionType}MentionId`]}
                const newRow = Object.assign({}, c, fantasticChartProps);
                expandedData.push(newRow);
            }
        }

    } else {
        // If no reactions data, still create rows with 0 values for consistency, but only for platform-specific reaction types
        const platformReactionTypes = getReactionTypesForPlatform(val.platformType);
        for (const reactionType of platformReactionTypes) {
            const reactionDisplayName = reactionTypeMapping[reactionType];
            const newRow = Object.assign({}, grouseResponse, {
                [val.platformType]: 0,
                reactionType: {id: reactionType, name: reactionDisplayName},
                'engagementType.id': reactionType,
                'engagementType.name': reactionDisplayName
            });
            expandedData.push(newRow);
        }
    }

    return expandedData;
}


export function expandPlatformReactionsCountRow(cleanQuery, reactionsForDay) {
    let expandedData = [];
    const val = getPlatform(cleanQuery.select, reactionsForDay);

    if (val.reactions) {
        const reactions = val.reactions;

        if (reactions && typeof reactions === 'object') {
            // Iterate through platform-specific reaction types and extract their count values
            const reactionTypes = getReactionTypesForPlatform(val.platformType);

            for (const reactionType of reactionTypes) {
                const reactionCount = reactions[reactionType] || 0;
                const reactionDisplayName = reactionTypeMapping[reactionType];
                const reactionMentionId = reactions[`${reactionType}MentionId`] || null;
                
                const fantasticChartProps = {
                    [val.platformType]: reactionCount,
                    reactionType: {id: reactionType, name: reactionDisplayName},
                    'engagementType.id': reactionType,
                    'engagementType.name': reactionDisplayName,
                    [`${reactionType}MentionId`]: reactionMentionId
                };

                const newRow = Object.assign({}, reactionsForDay, fantasticChartProps);
                expandedData.push(newRow);
            }
        }

    } else {
        // If no reactions data, still create rows with 0 values for consistency, but only for platform-specific reaction types
        const platformReactionTypes = getReactionTypesForPlatform(val.platformType);
        for (const reactionType of platformReactionTypes) {
            const reactionDisplayName = reactionTypeMapping[reactionType];
            const newRow = Object.assign({}, reactionsForDay, {
                [val.platformType]: 0,
                reactionType: {id: reactionType, name: reactionDisplayName},
                'engagementType.id': reactionType,
                'engagementType.name': reactionDisplayName
            });
            expandedData.push(newRow);
        }
    }
    
    return expandedData
}
